import { z } from "zod";
import {
  oneTimeSchemeSchema,
  sipSchemeSchema,
  stpSchemeSchema,
} from "../scheme";
import {
  convertToCurrency,
  getDecimalPlaces,
  parseAsCurrency,
} from "../../utils";
import { folioWiseHoldingSchema, schemeWiseHoldingSchema } from "../portfolio";
import { RedemptionBy } from "../../constants/transaction";
import { FolioWiseHolding } from "../../types/portfolio";
import { mandateSchema } from "../mandate";
import { planFrequencySchema, stepupFrequencySchema } from "./transaction";

const validateTransactionValue = (
  folio: FolioWiseHolding | null,
  redemptionBy: RedemptionBy | null,
  amount: string | null,
  units: string | null,
  ctx: z.RefinementCtx
) => {
  if (redemptionBy === RedemptionBy.UNITS) {
    if (!units) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Units are required",
        path: ["units"],
      });
    }
    if (Number(folio?.units) < Number(units)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Units should be less than or equal to ${Number(folio?.units).toFixed(3)}`,
        path: ["units"],
      });
    }
  }
  if (redemptionBy === RedemptionBy.AMOUNT) {
    if (!amount) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Amount is required",
        path: ["amount"],
      });
    }
    if (
      folio?.amount &&
      amount &&
      parseAsCurrency(folio.amount) < parseFloat(amount)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Amount should be less than or equal to ${convertToCurrency(parseAsCurrency(folio.amount), 2)}`,
        path: ["amount"],
      });
    }
  }
};

const validatePresence = (
  value: unknown,
  ctx: z.RefinementCtx,
  message: string
) => {
  if (!value) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message,
    });
  }
};

const isValidDate = (d: string) => {
  return !Number.isNaN(new Date(d).getTime());
};

const isDateInThePast = (d: string) => {
  const startDate = new Date(d);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return startDate >= today;
};

export const purchaseFormValidationSchema = z.object({
  schemes: z.array(
    z
      .object({
        scheme: oneTimeSchemeSchema.nullable().superRefine((d, ctx) => {
          validatePresence(d, ctx, "Scheme is required");
        }),
        folio_id: z.number().or(z.string()).nullable(),
        amount: z.string().superRefine((d, ctx) => {
          validatePresence(d, ctx, "Amount is required");
        }),
        collection: z
          .object({
            name: z.string(),
            description: z.string().optional(),
            type: z.literal("proposal").or(z.literal("basket")),
            id: z.number().optional(),
          })
          .passthrough()
          .nullable()
          .optional(),
      })
      .superRefine((d, ctx) => {
        const { scheme } = d;
        const amount = parseFloat(d.amount);
        const minAmount = d.folio_id
          ? scheme?.additional_purchase_min_amount || 0
          : scheme?.new_purchase_min_amount || 0;
        if (amount < minAmount && scheme?.product_type !== "SIF") {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Amount should be greater than or equal to ${convertToCurrency(minAmount.toString(), 2)}`,
            path: ["amount"],
          });
        }
        const maxAmount = parseFloat(
          scheme?.additional_purchase_max_amount || "0"
        );
        if (amount > maxAmount && maxAmount > 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Amount should be less than or equal to ${convertToCurrency(maxAmount.toString(), 2)}`,
            path: ["amount"],
          });
        }
        const multiplier = parseFloat(
          scheme?.purchase_amount_multiplier || "1"
        );
        // Convert to whole numbers to avoid floating-point precision issues
        const decimalPlaces = Math.max(
          getDecimalPlaces(amount),
          getDecimalPlaces(multiplier)
        );
        const scale = 10 ** decimalPlaces;
        const scaledAmount = Math.round(amount * scale);
        const scaledMultiplier = Math.round(multiplier * scale);

        if (scaledAmount % scaledMultiplier !== 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Amount should be in multiples of ${multiplier}`,
            path: ["amount"],
          });
        }
      })
  ),
});

export const sipFormValidationSchema = z
  .object({
    order_id: z.number().optional().nullable(),
    schemes: z.array(
      z
        .object({
          scheme: sipSchemeSchema.nullable().superRefine((d, ctx) => {
            validatePresence(d, ctx, "Scheme is required");
          }),
          folio_id: z.number().or(z.string()).nullable(),
          amount: z.string().superRefine((d, ctx) => {
            validatePresence(d, ctx, "Amount is required");
          }),
          collection: z
            .object({
              name: z.string(),
              description: z.string().optional(),
              type: z.literal("proposal").or(z.literal("basket")),
              id: z.number().optional(),
            })
            .passthrough()
            .nullable()
            .optional(),
        })
        .superRefine((d, ctx) => {
          const { scheme } = d;
          const amount = parseFloat(d.amount);
          const minAmount = parseFloat(
            scheme?.minimum_installment_amount || "0"
          );
          if (amount < minAmount) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `Amount should be greater than or equal to ${convertToCurrency(minAmount.toString(), 2)}`,
              path: ["amount"],
            });
          }
        })
    ),
    frequency: planFrequencySchema,
    mandate: mandateSchema
      .passthrough()
      .partial()
      .nullable()
      .superRefine((d, ctx) => {
        validatePresence(d, ctx, "Mandate is required");
      }),
    first_order_today: z.boolean(),
    start_date: z
      .string()
      .superRefine((d, ctx) => {
        validatePresence(d, ctx, "Start date is required");
      })
      .refine(isValidDate, {
        message: "Start date is invalid",
      })
      .refine(isDateInThePast, {
        message: "Start date cannot be in the past",
      }),
    step_up_amount: z.number().nullable(),
    step_up_percentage: z.number().nullable(),
    step_up_frequency: stepupFrequencySchema,
  })
  .superRefine((d, ctx) => {
    const { mandate, schemes } = d;
    for (let i = 0; i < schemes.length; i += 1) {
      const scheme = schemes[i];
      if (
        mandate?.mandate_limit &&
        Number(scheme.amount) > mandate.mandate_limit
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Amount should be less than or equal to ${convertToCurrency(mandate?.mandate_limit?.toString(), 2)}`,
          path: ["schemes", i, "amount"],
        });
      }
    }

    if (d.step_up_frequency) {
      if (!d.step_up_amount && !d.step_up_percentage) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Required",
          path: ["step_up_amount"],
        });
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Required",
          path: ["step_up_percentage"],
        });
      }
    }

    if (d.step_up_amount && Number(d.step_up_amount) <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Amount should be greater than 0",
        path: ["step_up_amount"],
      });
    }

    if (d.step_up_percentage && Number(d.step_up_percentage) <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Percentage should be greater than 0",
        path: ["step_up_percentage"],
      });
    }
  });

export const redemptionFormValidationSchema = z.object({
  schemes: z.array(
    z
      .object({
        scheme: schemeWiseHoldingSchema.nullable().superRefine((d, ctx) => {
          validatePresence(d, ctx, "Scheme is required");
        }),
        folio: folioWiseHoldingSchema.nullable().superRefine((d, ctx) => {
          validatePresence(d, ctx, "Folio is required");
        }),
        amount: z.string(),
        redemptionBy: z.nativeEnum(RedemptionBy),
        units: z.string(),
        redeemAllUnits: z.boolean().nullable().optional(),
      })
      .superRefine((d, ctx) => {
        const { folio, redemptionBy, amount, units, redeemAllUnits } = d;
        if (redeemAllUnits) return;
        validateTransactionValue(folio, redemptionBy, amount, units, ctx);
        const { scheme } = d;
        const redemptionMinAmount = scheme?.redemption_min_amount;
        const redemptionMaxAmount = scheme?.redemption_max_amount;
        const redemptionMinUnits = scheme?.redemption_min_units;
        const redemptionMaxUnits = scheme?.redemption_max_units;
        if (redemptionBy === RedemptionBy.AMOUNT) {
          if (
            redemptionMinAmount &&
            parseFloat(amount) < parseAsCurrency(redemptionMinAmount)
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `Amount should be greater than or equal to ${convertToCurrency(parseAsCurrency(redemptionMinAmount), 2)}`,
              path: ["amount"],
            });
          }
          if (
            redemptionMaxAmount &&
            parseFloat(amount) > parseAsCurrency(redemptionMaxAmount)
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `Amount should be less than or equal to ${convertToCurrency(parseAsCurrency(redemptionMaxAmount), 2)}`,
              path: ["amount"],
            });
          }
        }
        if (redemptionBy === RedemptionBy.UNITS) {
          if (
            redemptionMinUnits &&
            Number(units) < Number(redemptionMinUnits)
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `Units should be greater than or equal to ${Number(redemptionMinUnits).toFixed(2)}`,
              path: ["units"],
            });
          }
          if (
            redemptionMaxUnits &&
            Number(units) > Number(redemptionMaxUnits)
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `Units should be less than or equal to ${Number(redemptionMaxUnits).toFixed(2)}`,
              path: ["units"],
            });
          }
        }
      })
  ),
});

export const swpFormValidationSchema = z
  .object({
    withdrawal_by: z.nativeEnum(RedemptionBy).nullable(),
    amount: z.string().nullable(),
    units: z.string().nullable(),
    number_of_withdrawals: z
      .string()
      .or(z.number())
      .superRefine((d, ctx) => {
        validatePresence(d, ctx, "Number of withdrawals is required");
      }),
    start_date: z
      .string()
      .superRefine((d, ctx) => {
        validatePresence(d, ctx, "Start date is required");
      })
      .refine(isValidDate, {
        message: "Start date is invalid",
      })
      .refine(isDateInThePast, {
        message: "Start date cannot be in the past",
      }),
    frequency: planFrequencySchema,
    scheme: schemeWiseHoldingSchema.nullable().superRefine((d, ctx) => {
      validatePresence(d, ctx, "Scheme is required");
    }),
    folio: folioWiseHoldingSchema.nullable().superRefine((d, ctx) => {
      validatePresence(d, ctx, "Folio is required");
    }),
    first_order_today: z.boolean(),
  })
  .superRefine((d, ctx) => {
    const { folio, withdrawal_by, amount, units } = d;
    validateTransactionValue(folio, withdrawal_by, amount, units, ctx);
  });

export const stpFormValidationSchema = z
  .object({
    switch_out_scheme: schemeWiseHoldingSchema
      .nullable()
      .superRefine((d, ctx) => {
        validatePresence(d, ctx, "Scheme is required");
      }),
    folio: folioWiseHoldingSchema.nullable().superRefine((d, ctx) => {
      validatePresence(d, ctx, "Folio is required");
    }),
    switch_in_scheme: stpSchemeSchema.nullable().superRefine((d, ctx) => {
      validatePresence(d, ctx, "Scheme is required");
    }),
    number_of_transfers: z
      .string()
      .or(z.number())
      .superRefine((d, ctx) => {
        validatePresence(d, ctx, "Number of transfers is required");
      }),
    frequency: planFrequencySchema,
    transferBy: z.nativeEnum(RedemptionBy).nullable(),
    amount: z.string().nullable(),
    units: z.string().nullable(),
    start_date: z
      .string()
      .superRefine((d, ctx) => {
        validatePresence(d, ctx, "Start date is required");
      })
      .refine(isValidDate, {
        message: "Start date is invalid",
      })
      .refine(isDateInThePast, {
        message: "Start date cannot be in the past",
      }),
    first_order_today: z.boolean(),
  })
  .superRefine((d, ctx) => {
    const { folio, transferBy, amount, units } = d;
    validateTransactionValue(folio, transferBy, amount, units, ctx);
  });

export const switchInFormValidationSchema = z
  .object({
    switch_out_scheme: schemeWiseHoldingSchema
      .nullable()
      .superRefine((d, ctx) => {
        validatePresence(d, ctx, "Scheme is required");
      }),
    folio: folioWiseHoldingSchema.nullable().superRefine((d, ctx) => {
      validatePresence(d, ctx, "Folio is required");
    }),
    switch_in_scheme: oneTimeSchemeSchema.nullable().superRefine((d, ctx) => {
      validatePresence(d, ctx, "Scheme is required");
    }),
    switchBy: z.nativeEnum(RedemptionBy),
    amount: z.string().nullable(),
    units: z.string().nullable(),
    switchAllUnits: z.boolean().nullable().optional(),
  })
  .superRefine((d, ctx) => {
    const { folio, switchBy, amount, units, switchAllUnits } = d;
    if (switchAllUnits) return;
    validateTransactionValue(folio, switchBy, amount, units, ctx);
  });
